import { useTranslation } from 'react-i18next';

import {
  DETAILED_BOOKING_IMP_FIELDS,
  IMP_CONTEXTUAL_NODE_FIELDS,
} from '../../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../../constants/FormFieldsConstants';
import {
  IMPContextualNodeData,
  IMPContextualNodeErrors,
  SlotPackage,
} from '../../interface/DetailedBookingInterface';
import { Option } from '../../interface/FormFieldInterfaces';
import {
  FieldContainer,
  FieldInfoText,
  FieldLabel,
} from '../CreateBookingForm/CreateDetailedBookingForm.styles';
import { FormFieldWrapper } from '../FormFieldWrapper';

export interface PartialContextualFieldProps {
  contextualNodes: IMPContextualNodeData[];
  contextualNodeErrors: IMPContextualNodeErrors[];
  hasValidationBeenTriggered: boolean;
  handleContextualNodeChange: (
    nodeId: string,
    field: string,
    value: string
  ) => void;
  availableSegments: Option[];
  selectedSlotPackage: string;
  slotPackagesData: SlotPackage[];
}

interface ContextualNodeFieldIMPProps extends PartialContextualFieldProps {
  node: IMPContextualNodeData;
  fieldConfig: (typeof IMP_CONTEXTUAL_NODE_FIELDS)[number];
}

const ContextualNodeFieldIMP = ({
  node,
  fieldConfig,
  contextualNodes,
  contextualNodeErrors,
  hasValidationBeenTriggered,
  handleContextualNodeChange,
  availableSegments,
  selectedSlotPackage,
  slotPackagesData,
}: ContextualNodeFieldIMPProps) => {
  const { t } = useTranslation();

  const nodeIndex = contextualNodes.findIndex((n) => n.id === node.id);
  const nodeErrors = contextualNodeErrors[nodeIndex] || {};
  const fieldError = hasValidationBeenTriggered
    ? nodeErrors[fieldConfig.key as keyof typeof nodeErrors]
    : '';
  const handleOnChange = (value: string) =>
    handleContextualNodeChange(node.id, fieldConfig.key, value);

  const getFieldOptions = () => {
    if (fieldConfig.key === DETAILED_BOOKING_IMP_FIELDS.IM_SEGMENT) {
      // keeping unused segments and current selection for segments
      const filteredSegments = availableSegments.filter((segment) => {
        const isUsedByOtherNode = contextualNodes.some(
          (otherNode) =>
            otherNode.id !== node.id && otherNode.imSegment === segment.value
        );
        const isCurrentSelection = node.imSegment === segment.value;
        return !isUsedByOtherNode || isCurrentSelection;
      });

      return filteredSegments;
    }

    if ('options' in fieldConfig && Array.isArray(fieldConfig.options))
      return fieldConfig.options;

    return [];
  };

  const getSegmentPercentageInfo = () => {
    if (
      fieldConfig.key === DETAILED_BOOKING_IMP_FIELDS.IM_SEGMENT &&
      node.imSegment &&
      selectedSlotPackage &&
      !!slotPackagesData.length
    ) {
      const selectedPackage = slotPackagesData.find(
        (pkg) => pkg.slotPackageName === selectedSlotPackage
      );

      if (selectedPackage) {
        const selectedSegment = selectedPackage.segments.find(
          ({ segmentName }) => segmentName === node.imSegment
        );

        if (selectedSegment)
          return `(${t('available-slot-percentage')} : ${
            selectedSegment.percentageAvailable
          }%)`;
      }
    }
    return null;
  };

  const percentageInfo = getSegmentPercentageInfo();

  const isFieldDisabled =
    fieldConfig.key === FORM_FIELDS.IMPRESSIONS ||
    fieldConfig.key === FORM_FIELDS.APPROX_SHARE_BLOCKED_OVER_NODE;

  const getFieldValue = () => {
    const rawValue = node[fieldConfig.key as keyof IMPContextualNodeData];
    if (
      fieldConfig.key ===
        DETAILED_BOOKING_IMP_FIELDS.VALUE_TO_BE_LOCKED_AT_PACKAGE_LEVEL &&
      !rawValue
    )
      return '';
    return rawValue;
  };

  return (
    <FieldContainer key={fieldConfig.key} id={fieldConfig.key}>
      <FieldLabel>{t(fieldConfig.labelKey)}</FieldLabel>
      <FormFieldWrapper
        id={`${fieldConfig.key}-${fieldConfig.type}-${node.id}`}
        type={fieldConfig.type}
        value={getFieldValue()}
        onChange={handleOnChange}
        placeholder={fieldConfig.placeholderKey}
        options={getFieldOptions()}
        error={fieldError}
        disabled={isFieldDisabled}
      />
      {percentageInfo && <FieldInfoText>{percentageInfo}</FieldInfoText>}
    </FieldContainer>
  );
};

export default ContextualNodeFieldIMP;
